import {Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField} from '@mui/material';
import {AgGridReact} from 'ag-grid-react';
import * as React from 'react';
import {useContext, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ColDef, GridOptions, IServerSideGetRowsParams} from 'ag-grid-community';
import * as yup from "yup";
import {useAgGridLocale} from "../../../config/globalConfig";
import ContentBody from "../../sales_marketing/component/ContentBody";
import AgGridCustomTable from "../../../util/component/AgGridCustomTable";
import {useInvoiceRepository} from "../api/repositories/InvoiceRepository";
import AdvancedFilter from "./AdvancedFilter";
import {Close} from "@mui/icons-material";
import {addFilter, getFilterValue} from "../utils/FilterUtils";
import {atom, useRecoilValue, useSetRecoilState} from "recoil";
import {FilterDef} from "../constants/FinanceModels";
import FinanceDialog from "./FinanceDialog";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import {setSnackbar} from "../../../util/state/globalAction";
import {snackbarTypes} from "../../../util/state/globalVariables";
import {GlobalContext} from "../../../util/state/GlobalContextProvider";

interface ICustomerBaseDialogProps {
    open: boolean,
    onClose: () => void,
    onSelect?: (customer: any) => void
    invoiceId: string
}

const STATE_CUSTOMER_INVOICE_ACTIVE_FILTERS = atom<FilterDef[]>({
    key: 'customer-invoice-active-filters',
    default: []
});


const CustomerBaseDialog = (props: ICustomerBaseDialogProps) => {
    const {t} = useTranslation("finance", {keyPrefix: "invoices"});

    const agGridLocale = useAgGridLocale();
    const {dispatchGlobalData, globalData: {user}} = useContext(GlobalContext);

    const {getCustomer, reissueInvoiceNewCustomerId} = useInvoiceRepository();
    const gridRef = useRef<AgGridReact>(null);
    const setFilterDefs = useSetRecoilState(STATE_CUSTOMER_INVOICE_ACTIVE_FILTERS);
    const filterDefs = useRecoilValue(STATE_CUSTOMER_INVOICE_ACTIVE_FILTERS);
    const [openActionDialog, setOpenActionDialog] = useState(false);
    const [resetKey, setResetKey] = useState(0);
    const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);

    const validationSchema = yup.object().shape({
        cbCustomerId: yup.string().nullable(true).trim()
    })

    const defaultValues = {cbCustomerId: getFilterValue("cbCustomerId", filterDefs) || ""};

    const reset = () => {
        setFilterDefs([]);
        setSelectedCustomerId(null);
        setResetKey(prev => prev + 1);
    };

    const handleClose = () => {
        setFilterDefs([]);
        setSelectedCustomerId(null);
        props.onClose();
    };

    const openConfirmationDialog = () => {
        setOpenActionDialog(true);
    }

    useEffect(() => {
        if (!props.open) {
            setFilterDefs([]);
            setSelectedCustomerId(null);
        }
    }, [props.open, setFilterDefs]);

    function createDatasource(filters: any) {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                try {
                    const customerId: string = getFilterValue("cbCustomerId", filters);

                    if (!customerId || customerId.trim() === '') {
                        params.success({
                            rowData: [],
                            rowCount: 0
                        });
                        params.api.showNoRowsOverlay();
                        return;
                    }

                    const resp = await getCustomer(customerId);
                    let data = resp.data;

                    if (!data) {
                        params.success({
                            rowData: [],
                            rowCount: 0
                        });
                        params.api.showNoRowsOverlay();
                        return;
                    }

                    params.success({
                        rowData: [data],
                        rowCount: 1
                    });
                } catch (e) {
                    console.error('CustomerBaseDialog - Error fetching customer:', e);
                    params.fail();
                    params.api.showNoRowsOverlay();
                }
            }
        };
    }

    const columns: ColDef[] = [
        {
            field: 'bpCode',
            headerName: t('customer.bpCode'),
            checkboxSelection: true,
            minWidth: 200,
            unSortIcon: true, sortable: true
        },
        {
            field: 'registry.firstName',
            headerName: t('customer.firstName'),
            minWidth: 200,
            unSortIcon: true, sortable: true
        },
        {
            field: 'registry.lastName', headerName: t('customer.lastName'),
            minWidth: 200,
            unSortIcon: true, sortable: true,
        },
        {
            field: 'registry.businessName', headerName: t('customer.companyName'),
            minWidth: 200,
            unSortIcon: true, sortable: true
        },
        {
            field: 'registry.taxCode', headerName: t('customer.taxCode'),
            minWidth: 200,
            unSortIcon: true, sortable: true
        },
        {
            field: 'registry.vatNumber',
            headerName: t('customer.vatNumber'),
            minWidth: 200,
            unSortIcon: true,
            sortable: true
        },
        {
            headerName: t('customer.country'),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueGetter: (params: any) => {
                const invoicingAddr = params.data?.contactInfo?.addresses?.find((addr: any) => addr.addressType === 'INVOICING');
                return invoicingAddr?.country || '';
            }
        },
        {
            headerName: t('customer.province'),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueGetter: (params: any) => {
                const invoicingAddr = params.data?.contactInfo?.addresses?.find((addr: any) => addr.addressType === 'INVOICING');
                return invoicingAddr?.province || '';
            }
        },
        {
            headerName: t('customer.city'),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueGetter: (params: any) => {
                const invoicingAddr = params.data?.contactInfo?.addresses?.find((addr: any) => addr.addressType === 'INVOICING');
                return invoicingAddr?.city || '';
            }
        },
        {
            headerName: t('customer.zipCode'),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueGetter: (params: any) => {
                const invoicingAddr = params.data?.contactInfo?.addresses?.find((addr: any) => addr.addressType === 'INVOICING');
                return invoicingAddr?.zipCode || '';
            }
        },
        {
            headerName: t('customer.street'),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueGetter: (params: any) => {
                const invoicingAddr = params.data?.contactInfo?.addresses?.find((addr: any) => addr.addressType === 'INVOICING');
                return invoicingAddr?.street || '';
            }
        },
        {
            headerName: t('customer.houseNumber'),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueGetter: (params: any) => {
                const invoicingAddr = params.data?.contactInfo?.addresses?.find((addr: any) => addr.addressType === 'INVOICING');
                return invoicingAddr?.houseNumber || '';
            }
        },
        {
            headerName: t('customer.addrEmail'),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueGetter: (params: any) => {
                const invoicingAddr = params.data?.contactInfo?.addresses?.find((addr: any) => addr.addressType === 'INVOICING');
                return invoicingAddr?.addrEmail || '';
            }
        },
    ];


    const gridOptions: GridOptions = {
        rowSelection: 'single',
        suppressRowClickSelection: true,
        domLayout: 'autoHeight',
        rowModelType: 'serverSide',
        pagination: false,
        cacheBlockSize: 10,
        defaultColDef: {
            sortable: false,
            resizable: true,
            wrapHeaderText: true
        },
        columnDefs: columns,
        localeText: agGridLocale,
        onSelectionChanged: () => {
            const selectedRows = gridRef.current?.api?.getSelectedRows();
            if (selectedRows && selectedRows.length > 0) {
                const customer = selectedRows[0];
                setSelectedCustomerId(customer.bpCode);
                if (props.onSelect) {
                    props.onSelect(customer);
                }
            } else {
                setSelectedCustomerId(null);
            }
        }
    };

    const renderCustomerId = (field: any) => (
        <TextField
            fullWidth
            label={t('customer.customerId')}
            value={field.value}
            onChange={field.onChange}
        />
    );
    useEffect(() => {
        if (props.open && gridRef?.current?.api) {
            gridRef.current.api.setServerSideDatasource(createDatasource(filterDefs));
        }
    }, [filterDefs, props.open]);


    const fields = {
        cbCustomerId: renderCustomerId
    }

    const submit = (data: any) => {
        addFilter(setFilterDefs, "cbCustomerId", data.cbCustomerId);
    };

    return (
        <>
            <Dialog
                open={props.open}
                onClose={handleClose}
                fullWidth
                maxWidth="lg"
            >
                <IconButton sx={{position: 'absolute', top: 12, right: 12}} onClick={handleClose}>
                    <Close fontSize="inherit"/>
                </IconButton>

                <DialogTitle>
                    {t('customer.selectCustomer')}
                </DialogTitle>
                <DialogContent>
                    <ContentBody>
                        <AdvancedFilter key={resetKey} schema={validationSchema} mainFields={fields}
                                        defaultValues={defaultValues}
                                        onSubmit={submit} onReset={reset}/>

                        <AgGridCustomTable gridOptions={gridOptions} gridRef={gridRef}/>
                    </ContentBody>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="accent" variant="outlined">
                        {t('action-dialog.cancel')}
                    </Button>
                    <Button
                        onClick={openConfirmationDialog}
                        color="accent"
                        variant="contained"
                        disabled={!selectedCustomerId}
                    >
                        {t('action-dialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
            <FinanceDialog open={openActionDialog}
                           content={t('action-dialog.reissue.textReissueNewCustomer')}
                           onClose={() => setOpenActionDialog(false)}
                           titleIcon={<InfoRoundedIcon sx={{color: 'accent.main'}}/>}
                           title={t('action-dialog.reissue.attention')}
                           confirmButton={t('action-dialog.confirm')}
                           cancelButton={t('action-dialog.cancel')}
                           callback={() => {
                               if (selectedCustomerId) {
                                   reissueInvoiceNewCustomerId(props.invoiceId, selectedCustomerId).then(response => {
                                       if(response.status === "OK") {
                                           dispatchGlobalData({
                                               type: setSnackbar,
                                               payload: {
                                                   snackbar: {
                                                       message: t("action-dialog.reissue.completeReissueNewCustomer", {customerId: selectedCustomerId}),
                                                       type: snackbarTypes.success,
                                                       show: true,
                                                       autoClose: true
                                                   }
                                               }
                                           })
                                       } else {
                                           dispatchGlobalData({
                                               type: setSnackbar,
                                               payload: {
                                                   snackbar: {
                                                       message: t("action-dialog.reissue." + response.message),
                                                       type: snackbarTypes.error,
                                                       show: true,
                                                       autoClose: true
                                                   }
                                               }
                                           });
                                       }
                                   })
                               }
                               handleClose();
                           }}
            />
        </>
    );
};

export default CustomerBaseDialog;